// frontend/src/pages/AppProductsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../utils/authFetch";
import TornaAlMenu from "../components/TornaAlMenu";

const API_URL = process.env.REACT_APP_API_URL || "";


const CART_KEY = "dzr_app_cart";

const HIDDEN_APP_CATEGORIES = new Set(["APERITIVO", "CAFFETTERIA", "CALICE DI VINO", "DOLCE E SALATO", "GELATI ALGIDA", "APERITIVO,DOLCE E SALATO"]);

const formatPrice = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString("it-IT", {
    style: "currency",
    currency: "EUR",
  });
};

const availabilityInfo = (p) => {
  const status = String(p?.availability_status || "AVAILABLE").toUpperCase();
  if (status === "COMING_SOON") {
    return { status, label: "In arrivo", emoji: "🟠", canBuy: false };
  }
  if (status === "UNAVAILABLE") {
    return { status, label: "Non disponibile", emoji: "🔴", canBuy: false };
  }
  return { status: "AVAILABLE", label: "Disponibile", emoji: "🟢", canBuy: true };
};

const displayCategoryName = (name) => {
  const raw = String(name || "").trim();
  const up = raw.toUpperCase();

  if (up === "ALIMENTI DALLO ZIO ROB") return "ALIMENTI DALLO ZIO ROBY";
  if (up === "APERITIVO;DOLCE E SALATO") return "APERITIVO\nDOLCE E SALATO";

  return raw.replace(/;/g, "\n");
};

const categoryIcon = (name) => {
  const c = String(name || "").toUpperCase();
  if (!c) return "▦";
  if (c.includes("ALIMENT")) return "🍝";
  if (c.includes("BIBIT") || c.includes("BEVAND")) return "🥤";
  if (c.includes("BIRR")) return "🍺";
  if (c.includes("VINO") || c.includes("PIOVESAN") || c.includes("GALBERTI") || c.includes("BORGOMOLINO") || c.includes("CANTINA")) return "🍷";
  if (c.includes("LIQUOR") || c.includes("GRAPP")) return "🥃";
  if (c.includes("BAG IN BOX")) return "📦";
  if (c.includes("REGALO") || c.includes("CONFEZ")) return "🎁";
  if (c.includes("OGGETT")) return "🛍️";
  return "▦";
};

export default function AppProductsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [addedProductId, setAddedProductId] = useState(null);

  const refreshCartCount = () => {
    try {
      const cart = JSON.parse(localStorage.getItem(CART_KEY) || "[]");
      setCartCount(cart.reduce((sum, x) => sum + Number(x.qty || 1), 0));
    } catch {
      setCartCount(0);
    }
  };

  const addProductToCart = (product) => {
    const av = availabilityInfo(product);
    if (!av.canBuy) return;

    const item = {
      id: product.id,
      cart_id: `product-${product.id}`,
      type: "product",
      code: product.category || "PRODOTTO",
      title: product.name,
      subtitle: product.unit_type === "litri"
        ? `${product.litri_per_unita || "0"} L`
        : `${product.pezzi_per_unita || 1} pz`,
      price: product.prezzo,
      product_id: product.id,
      image_url: product.image_url || "",
    };

    let current = [];
    try {
      current = JSON.parse(localStorage.getItem(CART_KEY) || "[]");
    } catch {
      current = [];
    }

    const exists = current.find((x) =>
      String(x.cart_id || `${x.type || "offer"}-${x.id}`) === item.cart_id
    );

    const next = exists
      ? current.map((x) =>
          String(x.cart_id || `${x.type || "offer"}-${x.id}`) === item.cart_id
            ? { ...x, qty: Number(x.qty || 1) + 1 }
            : x
        )
      : [...current, { ...item, qty: 1 }];

    localStorage.setItem(CART_KEY, JSON.stringify(next));
    refreshCartCount();

    setAddedProductId(product.id);
    setTimeout(() => setAddedProductId(null), 650);
  };

  useEffect(() => {
    if (!selectedProduct) return;
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = oldOverflow;
    };
  }, [selectedProduct]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_URL}/api/app/products/`);
      const json = await res.json();
      setRows(Array.isArray(json) ? json : json.results || []);
    } catch (e) {
      console.error(e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    refreshCartCount();
  }, []);

  const categories = useMemo(() => {
    return Array.from(
      new Set(
        rows
          .map((x) => String(x.category || "").trim())
          .filter((c) => c && !HIDDEN_APP_CATEGORIES.has(c.toUpperCase()))
      )
    ).sort();
  }, [rows]);

  const visibleRows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter((x) => {
      const rawCategory = String(x.category || "").trim();
      if (HIDDEN_APP_CATEGORIES.has(rawCategory.toUpperCase())) return false;
      const haystack = [
        x.name,
        x.category,
        displayCategoryName(x.category),
        x.unit_type,
        x.litri_per_unita ? `${x.litri_per_unita} litri ${String(x.litri_per_unita).replace(".", ",")} l` : "",
        x.pezzi_per_unita ? `${x.pezzi_per_unita} pezzi pz` : "",
      ].join(" ").toLowerCase();

      const normalizedTerm = term
        .replace(",", ".")
        .replace(/\bbirre\b/g, "birr")
        .replace(/\bbirra\b/g, "birr")
        .replace(/\bgrappe\b/g, "grapp")
        .replace(/\bgrappa\b/g, "grapp")
        .replace(/\blitro\b/g, "litri")
        .replace(/\bl\b/g, "litri");

      const normalizedHaystack = haystack.replace(",", ".");

      const matchQ = !term || normalizedHaystack.includes(normalizedTerm) || haystack.includes(term);
      const matchCategory = !category || rawCategory === category;
      return matchQ && matchCategory;
    });
  }, [rows, q, category]);

  return (
    <div className="container mt-4">
      <TornaAlMenu to="/app" />

      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <div>
          <h3 className="m-0">🛒 Catalogo prodotti</h3>
          <div className="text-muted small">
            Scegli i prodotti disponibili e aggiungili al carrello.
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2">
          <input
            className="form-control"
            placeholder="Cerca prodotto…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ minWidth: 220 }}
          />

        </div>
      </div>

        {!loading && categories.length > 0 && (
          <div className="mb-4">
            <div className="fw-bold mb-2" style={{ color: "#211014" }}>Categorie</div>
            <div
              className="d-flex gap-3 overflow-auto pb-2 pe-4"
              style={{ WebkitOverflowScrolling: "touch", maxWidth: "92vw", marginRight: -28 }}
            >
              <button
                type="button"
                className="btn flex-shrink-0 rounded-4"
                onClick={() => setCategory("")}
                style={{
                  width: 104,
                  minHeight: 104,
                  background: !category ? "#5b0f1b" : "#fff8f1",
                  color: !category ? "#fff" : "#5b0f1b",
                  border: "1px solid rgba(91,15,27,0.25)",
                  fontWeight: 800,
                  boxShadow: !category ? "0 10px 24px rgba(91,15,27,0.22)" : "0 8px 20px rgba(91,15,27,0.08)",
                }}
              >
                <div style={{ fontSize: 28, lineHeight: 1 }}>▦</div>
                <div className="mt-2">Tutto</div>
              </button>

              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  className="btn flex-shrink-0 rounded-4"
                  onClick={() => setCategory(c)}
                  style={{
                    width: 136,
                    minHeight: 104,
                    background: category === c ? "#5b0f1b" : "#fff8f1",
                    color: category === c ? "#fff" : "#5b0f1b",
                    border: "1px solid rgba(91,15,27,0.25)",
                    fontWeight: 800,
                    boxShadow: category === c ? "0 10px 24px rgba(91,15,27,0.22)" : "0 8px 20px rgba(91,15,27,0.08)",
                    lineHeight: 1.08,
                    whiteSpace: "normal",
                    overflowWrap: "normal",
                    wordBreak: "keep-all",
                  }}
                >
                  <div style={{ fontSize: 28, lineHeight: 1 }}>{categoryIcon(c)}</div>
                  <div className="mt-2" style={{ fontSize: "0.78rem", whiteSpace: "pre-line" }}>{displayCategoryName(c)}</div>
                </button>
              ))}
            </div>
          </div>
        )}

      {loading ? (
        <p>Caricamento…</p>
      ) : visibleRows.length === 0 ? (
        <div className="alert alert-info rounded-4">
          Nessun prodotto disponibile al momento.
        </div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {visibleRows.map((x) => {
            const av = availabilityInfo(x);
            return (
              <div
                key={x.id}
                className="card border-0 rounded-5 overflow-hidden"
                role="button"
                onClick={() => setSelectedProduct(x)}
                style={{
                  boxShadow: "0 14px 34px rgba(91,15,27,0.12)",
                  cursor: "pointer",
                  background: "linear-gradient(180deg, #ffffff 0%, #fff8f1 100%)",
                }}
              >
                <div className="card-body p-3">
                  <div className="d-flex gap-3 align-items-stretch">
                    <div
                      className="rounded-4 flex-shrink-0 overflow-hidden"
                      style={{
                        width: 128,
                        minHeight: 150,
                        background: x.image_url
                          ? "linear-gradient(135deg, #fff8f1 0%, #f4ebe2 100%)"
                          : "linear-gradient(135deg, #5b0f1b 0%, #8b1e2d 55%, #211014 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {x.image_url ? (
                        <img
                          src={x.image_url}
                          alt={x.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div style={{ fontSize: 48 }}>🍷</div>
                      )}
                    </div>

                    <div className="flex-grow-1 d-flex flex-column" style={{ minWidth: 0 }}>
                      <div className="mb-2">
                        <span
                          className="badge rounded-pill px-3 py-2"
                          style={{
                            background: "#5b0f1b",
                            color: "#fff",
                            letterSpacing: 0.2,
                            fontSize: "0.58rem",
                            maxWidth: "100%",
                            whiteSpace: "nowrap",
                            lineHeight: 1.1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {displayCategoryName(x.category) || "PRODOTTO"}
                        </span>
                      </div>

                      <h4 className="mb-1 fw-bold" style={{ color: "#211014", fontSize: "1.28rem", lineHeight: 1.05 }}>
                        {x.name}
                      </h4>

                      <div className="small text-muted mb-1">
                        {x.unit_type === "litri"
                          ? `${x.litri_per_unita || "0"} L`
                          : `${x.pezzi_per_unita || 1} pz`}
                      </div>

                      <div className="fs-4 fw-bold mb-1" style={{ color: "#5b0f1b" }}>
                        {formatPrice(x.prezzo)}
                      </div>

                      <div className="small fw-semibold mb-2">
                        {av.emoji} {av.label}
                      </div>

                      <button
                        className="btn w-100 mt-auto rounded-pill fw-bold py-2"
                        style={{
                          background: addedProductId === x.id ? "#198754" : (av.canBuy ? "#5b0f1b" : "#ddd"),
                          color: av.canBuy ? "#fff" : "#777",
                          border: 0,
                          cursor: av.canBuy ? "pointer" : "not-allowed",
                          transform: addedProductId === x.id ? "scale(0.98)" : "scale(1)",
                          transition: "all .18s ease",
                        }}
                        disabled={!av.canBuy}
                        onClick={(e) => {
                          e.stopPropagation();
                          addProductToCart(x);
                        }}
                      >
                        {av.canBuy ? (addedProductId === x.id ? "✅ Aggiunto!" : "🛒 Aggiungi al carrello") : `${av.emoji} ${av.label}`}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        className="btn rounded-circle shadow-lg"
        onClick={() => navigate("/app/carrello")}
        style={{
          position: "fixed",
          right: 18,
          bottom: 18,
          width: 72,
          height: 72,
          background: "linear-gradient(135deg, #5b0f1b 0%, #9b1328 100%)",
          color: "#fff",
          border: 0,
          zIndex: 1050,
          fontSize: 30,
        }}
      >
        🛒
        {cartCount > 0 && (
          <span
            className="badge rounded-circle bg-danger text-white"
            style={{
              position: "absolute",
              right: -4,
              top: -4,
              minWidth: 28,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
            }}
          >
            {cartCount}
          </span>
        )}
      </button>

      {selectedProduct && (() => {
        const selectedAv = availabilityInfo(selectedProduct);
        return (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ background: "rgba(0,0,0,0.45)", overflowY: "auto", overscrollBehavior: "contain" }}
          onClick={() => setSelectedProduct(null)}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-lg"
            style={{ maxHeight: "96vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content border-0 rounded-4 overflow-hidden" style={{ maxHeight: "94vh" }}>
              <div
                className="d-flex align-items-center justify-content-center bg-light"
                style={{ height: 320 }}
              >
                {selectedProduct.image_url ? (
                  <img
                    src={selectedProduct.image_url}
                    alt={selectedProduct.name}
                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                  />
                ) : (
                  <div className="text-center text-muted">
                    <div style={{ fontSize: 70 }}>🍷</div>
                    <div>Nessuna immagine disponibile</div>
                  </div>
                )}
              </div>

              <div
                className="modal-body p-4"
                style={{
                  maxHeight: "calc(94vh - 320px)",
                  overflowY: "auto",
                  WebkitOverflowScrolling: "touch",
                  overscrollBehavior: "contain",
                }}
              >
                <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                  <div>
                    <span className="badge rounded-pill text-bg-dark mb-2">
                      {displayCategoryName(selectedProduct.category) || "PRODOTTO"}
                    </span>
                    <div className="small fw-semibold mb-2">
                      {selectedAv.emoji} {selectedAv.label}
                    </div>
                    <h2 className="fw-bold mb-1">{selectedProduct.name}</h2>
                    <div className="text-muted">
                      {selectedProduct.unit_type === "litri"
                        ? `${selectedProduct.litri_per_unita || "0"} L`
                        : `${selectedProduct.pezzi_per_unita || 1} pz`}
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Chiudi"
                    onClick={() => setSelectedProduct(null)}
                  />
                </div>

                {selectedProduct.description && (
                  <div className="mb-4">
                    <div className="fw-semibold mb-1">Descrizione</div>
                    <div className="text-muted" style={{ whiteSpace: "pre-line" }}>
                      {selectedProduct.description}
                    </div>
                  </div>
                )}

                <div className="fs-3 fw-bold mb-4">
                  {formatPrice(selectedProduct.prezzo)}
                </div>

                <div className="d-flex flex-wrap gap-2">
                  <button
                    className="btn btn-lg rounded-pill fw-bold px-4"
                    style={{
                      background: selectedAv.canBuy ? "#5b0f1b" : "#ddd",
                      color: selectedAv.canBuy ? "#fff" : "#777",
                      border: 0,
                      cursor: selectedAv.canBuy ? "pointer" : "not-allowed",
                    }}
                    disabled={!selectedAv.canBuy}
                    onClick={() => {
                      if (!selectedAv.canBuy) return;
                      addProductToCart(selectedProduct);
                      setSelectedProduct(null);
                    }}
                  >
                    {selectedAv.canBuy ? (addedProductId === selectedProduct?.id ? "✅ Aggiunto!" : "🛒 Aggiungi al carrello") : `${selectedAv.emoji} ${selectedAv.label}`}
                  </button>

                  <button
                    className="btn btn-outline-secondary btn-lg rounded-pill px-4"
                    onClick={() => setSelectedProduct(null)}
                  >
                    Continua a guardare
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}
